import React, { useState, useEffect, useRef } from 'react';
import {Box,Modal,Typography} from '@mui/material';
import { osVersion, mobileModel, deviceType, isTablet } from 'react-device-detect';
import { v4 as uuidv4 } from 'uuid';
import { useHistory, useParams } from 'react-router-dom';
import ShowToast from "../components/ToastComponent";
import { LOCAL_STORAGE, LS_UNIQUE_DEVICE_ID } from '../assets/constants/BrowserStorageKeys';
import { BUTTON_PRIMARY, SUCCESS, TOAST_SUCCESS, TOAST_ERROR } from "../assets/constants/Constants";
import { ACCESS_LOCKER_BANK, HOME, LINK_MY_DEVICE } from '../assets/constants/PageList';
import { SUCCESS_PIN } from "../assets/constants/Icons";
import { getTenantAndAssignmentSettings, linkMyDevice } from '../api/api';
import { getSecureItemFromSpecificStorage, storeSecureItemInSpecifiedStorage } from '../lib/BrowserStorageAccessMiddleware';
import { getPathForNextPage, checkIfAssignmentIsProperlyConfigured, handleStoreUserDataInLocalStorage, setDomainAndLockerBankIdInLocalStorage } from '../lib/Utils';
import { useTranslation } from 'react-i18next';
import CircularProgressLoader from "../components/CircularProgressLoader";
import CustomDialog from '../components/CustomDialog';
import CustomQRCodeReader from '../components/CustomQRCodeReader';

const LinkMyDevicePage = () => {
    const [modalVisible, setModalVisible] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [loaderVisible, setLoaderVisible] = useState(false);
    const history = useHistory();
    const { domainLockerBankAndPayloadInfoInBase64 } = useParams();
    const { t } = useTranslation();
    const isInitialRender = useRef(true);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Loading. Please wait.');
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [dialogContentText, setDialogContentText] = useState('');

    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            extractPayloadValidateAndRedirectUser();
        }
        const backAction = () => {
        };

        window.addEventListener('beforeunload', backAction);

        return () => window.removeEventListener('beforeunload', backAction);
    }, []);

    const extractPayloadValidateAndRedirectUser = async () => {
        try {
            const domainLockerBankAndPayloadInfo = window.atob(domainLockerBankAndPayloadInfoInBase64);
            const domainLockerBankAndPayloadInfoObject = JSON.parse(domainLockerBankAndPayloadInfo);
            setDomainAndLockerBankIdInLocalStorage({
                domain: domainLockerBankAndPayloadInfoObject.domain,
                lockerBankId: domainLockerBankAndPayloadInfoObject.lockerBankId
            });
            const isAssignmentProperlyConfigured = await getSettingsAndCheckIfAssignmentIsProperlyConfigured(domainLockerBankAndPayloadInfoObject.lockerBankId);

            if ( isAssignmentProperlyConfigured !== true ) {
                setIsLoading(false);
                setDialogContentText(isAssignmentProperlyConfigured.errorMessage);
                setIsDialogVisible(true);
                return;
            }

            const linkMyDeviceResponse = await linkMyDevice(domainLockerBankAndPayloadInfoObject?.encrypted_payload, getUniqueDeviceId());

            if (linkMyDeviceResponse?.status === SUCCESS) {
                handleStoreUserDataInLocalStorage(linkMyDeviceResponse?.data);
                setIsLoading(false);
                ShowToast('Device Linked Successfully.', TOAST_SUCCESS);

                history.replace({
                    pathname: getPathForNextPage(LINK_MY_DEVICE, HOME)
                });
            }
        } catch (error) {
            console.error(`Error during payload validation: `, error);
            setIsLoading(false);
            setDialogContentText('Error during link my phone. Please try again.');
            setIsDialogVisible(true);
        }
    }

    const getSettingsAndCheckIfAssignmentIsProperlyConfigured = async (lockerBankId) => {
        try {
            const response = await getTenantAndAssignmentSettings(lockerBankId);

            if (response?.status === SUCCESS) {
                return await checkIfAssignmentIsProperlyConfigured(response?.data);
            }
        } catch (error) {
            console.error('Error while settings validation: ', error);
        }
    }

    const getUniqueDeviceId = () => {
        const existingUniqueDeviceId = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_UNIQUE_DEVICE_ID);

        if (existingUniqueDeviceId) {
            return existingUniqueDeviceId;
        }

        const newUniqueDeviceId = uuidv4();
        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LS_UNIQUE_DEVICE_ID, newUniqueDeviceId);
        return newUniqueDeviceId;
    }

    const closeErrorDialogAndRedirectUser = () => {
        setIsDialogVisible(false);

        history.replace({
            pathname: '/'
        });
    }

    const onSuccess = e => {
        if(e){
            setShowLoader(true);
            const base64regex =
                /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
    
            function isBase64(str) {
                if (str === '' || str.trim() === '') {
                    return false;
                }
                try {
                    return base64regex.test(str) && /^[\x00-\x7F]*$/.test(str);
                } catch (err) {
                    return false;
                }
            }
    
            if (isBase64(e.data)) {
                const scanResult = "" //base64.decode(e.data);
                const newResult = JSON.parse(scanResult);
    
                if (newResult) {
                    isObject(newResult);
                }
    
                function isObject(val) {
                    if (typeof val === 'object') {
                        const Domain = newResult['domain'];
                        const QRString = newResult['linking_key'];
    
                        if (QRString !== null && QRString !== undefined) {
                            if (Domain !== null) {
                                const domain = extractDomain(Domain);
                                localStorage.setItem('domain', domain);
                            }
                            getDeviceLinked(QRString);
                        } else {
                            ShowToast(t('qrcode_linkphone_error'), TOAST_ERROR);
                            navigateToLoginScreen();
                        }
                    } else {
                        ShowToast(t('phone_link_error'), TOAST_ERROR);
                        navigateToLoginScreen();
                    }
                }
            } else {
                ShowToast(t('incorrect_qrcode'), TOAST_ERROR);
                navigateToLoginScreen();
            }
        }
    };

    const extractDomain = (url) => {
        url = url.replace(/(^\w+:|^)\/\//, '');
        const parts = url.split('/');
        return parts[0];
    };

    const getDeviceLinked = async QRString => {
        setShowLoader(false);
        try {
            setLoaderVisible(true);
            const domain = localStorage.getItem('domain');
            const deviceId = uuidv4();
            const deviceModel = mobileModel;
            const deviceOSVersion = osVersion;
            const appVersion = '1.0.0'; // Assuming static app version
            const device_type = deviceType;
            const is_tablet = isTablet;
            const fcmToken = 'dummy_fcm_token'
            
            const deviceData = {
                linking_key: QRString,
                env: "",//App,
                device_id: deviceId,
                device_model: deviceModel,
                os_version: deviceOSVersion,
                app_version: appVersion,
                device_type: device_type,
                is_tablet: is_tablet,
                device_fcm: fcmToken
            };

            if (domain !== null) {
                const response = "" //await linkMyPhone(domain, deviceData);
                if (response) {
                    localStorage.setItem('User_id', JSON.stringify(response.user_id));
                    localStorage.setItem('lockerbank_id', JSON.stringify(response.bank_id));
                    localStorage.setItem('token', response.accesstoken);
                    localStorage.setItem('userInfo', JSON.stringify(response));
                    let user = '';
                    if (response.email != null && response.email !== '') {
                        user = response.email;
                    } else if (response.mobile_number != null && response.mobile_number !== '') {
                        user = response.mobile_number;
                    } else {
                        user = response.username;
                    }
                    localStorage.setItem('user', user);
                    setModalVisible(true);
                    navigateToDashboard();
                    setTimeout(() => {
                        setLoaderVisible(false);
                        setModalVisible(false);
                    }, 2000);
                } else {
                    setLoaderVisible(false);
                    navigateToLoginScreen();
                }
            }
        } catch (error) {
            setLoaderVisible(false);
            navigateToLoginScreen();
            console.error('connect Mobile catch Error while link device:', error);
        }
    };

    const navigateToLoginScreen = () => {
        setTimeout(() => {
            history.push('/login')
        }, 3000);
    }

    const navigateToDashboard = () => {
        setTimeout(() => {
            history.push('/dashboard')
        }, 2000);
    }

    const onScanError = (error) => {
        console.log("errro",error)
    }

    return (
        <div className='connect-mobile-page'>
            <div className='main-container'>
                <div className='main'>
                    {isLoading && (
                        <CircularProgressLoader message={loadingMessage} />
                    )}
                    <CustomDialog
                        dialogId='error-dialog'
                        dialogVisible={isDialogVisible}
                        dialogTitle={t('page_title_error')}
                        dialogContentText={t(dialogContentText)}
                        buttonTwoTitle={t('button_ok')}
                        buttonProps={{ buttonTwo: {type: BUTTON_PRIMARY}}}
                        handleAccept={closeErrorDialogAndRedirectUser}
                    />
                </div>
            </div>
        </div>
    );
};

export default LinkMyDevicePage;